import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  HostListener,
  inject,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { toSignal } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AuthService, ProfileService, UserRole } from '@nebalkon-workspace/auth';
import { catchError, firstValueFrom, of } from 'rxjs';
import { CABINET_DIALOG } from '@nebalkon-workspace/cabinet-core';
import { NiimbotPrinterService } from '@nebalkon-workspace/niimbot-printer';
import { PhotoUploadService } from '@nebalkon-workspace/media-api';
import {
  CLIENT_ITEM_DESCRIPTION_MAX_LENGTH,
  CLIENT_ITEM_NAME_MAX_LENGTH,
  CabinetButtonComponent,
  CabinetItemPhotoPreviewComponent,
  CabinetPhotoUploaderComponent,
  ClientItemTagsControlComponent,
  ClientItemTagsChipsComponent,
  isDeletableCatalogItem,
  itemDetailDimensionsLabel,
  itemEffectiveDimensionsScope,
  itemPlacementAddresses,
  itemPlacementLabelKey,
  itemQuantityBadge,
  itemStatusDetailBadgeClass,
  itemStatusLabelKey,
  ITEM_MAX_QUANTITY,
  ITEM_MIN_QUANTITY,
  ITEM_MAX_PARTS_COUNT,
  ITEM_MAX_PHOTOS_TOTAL,
  ITEM_MIN_PARTS_COUNT,
  normalizeItemQuantity,
  previewBillableVolumeM3,
} from '@nebalkon-workspace/cabinet-client';
import type { AdminUserItemResponse } from '@nebalkon-workspace/admin-api';
import { UserAdminService } from '@nebalkon-workspace/admin-api';
import {
  CellCoord,
  ItemDimensionsScope,
  ItemService,
  StorageStatus,
  type UpdateStaffItemRequest,
} from '@nebalkon-workspace/storage-api';
import { startWith } from 'rxjs/operators';
import {
  NotButton,
  NotInput,
  NotModal,
  NotSystemNotificationService,
} from '@nebalkon-workspace/ui-kit';
import { printStaffEntityQr } from '../utils/staff-entity-qr-print.util';
import { resolveOwnerLastName } from '../utils/resolve-owner-last-name.util';
import { StaffWarehouseCellPickerComponent } from './staff-warehouse-cell-picker.component';
import { cellCoordsFromRefs } from './staff-warehouse-cell-picker.util';
import type { StaffUserItemDetailModalData } from './staff-user-item-detail-modal.types';

type PlacementPanelMode = 'place' | 'move';

@Component({
  selector: 'lib-staff-user-item-detail-modal',
  imports: [
    NotModal,
    NotButton,
    CabinetButtonComponent,
    CabinetItemPhotoPreviewComponent,
    CabinetPhotoUploaderComponent,
    StaffWarehouseCellPickerComponent,
    NotInput,
    ReactiveFormsModule,
    TranslatePipe,
    ClientItemTagsControlComponent,
    ClientItemTagsChipsComponent,
  ],
  templateUrl: './staff-user-item-detail-modal.component.html',
  styleUrl: './staff-user-item-detail-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StaffUserItemDetailModalComponent implements OnInit {
  readonly data = inject(DIALOG_DATA) as StaffUserItemDetailModalData;
  readonly #dialogRef = inject(DialogRef);
  readonly #photoUpload = inject(PhotoUploadService);
  readonly #fb = inject(FormBuilder);
  readonly #destroyRef = inject(DestroyRef);
  readonly #printer = inject(NiimbotPrinterService);
  readonly #translate = inject(TranslateService);
  readonly #notification = inject(NotSystemNotificationService);
  readonly #confirmDialog = inject(CABINET_DIALOG);
  readonly #auth = inject(AuthService);
  readonly #profile = inject(ProfileService);
  readonly #itemService = inject(ItemService);
  readonly #userAdmin = inject(UserAdminService);

  readonly photos = signal<string[]>([]);
  readonly photosLoading = signal(true);
  readonly activePhotoIndex = signal(0);
  readonly updating = signal(this.data.updating);
  readonly editMode = signal(false);
  readonly savingItem = signal(false);
  readonly deletingItem = signal(false);
  readonly printingQr = signal(false);
  readonly item = signal(this.data.item);
  readonly placementPanelOpen = signal(false);
  readonly placementPanelMode = signal<PlacementPanelMode>('place');
  readonly placementSubmitting = signal(false);
  readonly clearingPlacement = signal(false);
  readonly itemTags = signal<string[]>([]);

  protected readonly maxPhotos = ITEM_MAX_PHOTOS_TOTAL;
  readonly photoUploader = viewChild(CabinetPhotoUploaderComponent);

  protected readonly itemStatusLabelKey = itemStatusLabelKey;
  protected readonly itemPlacementAddresses = itemPlacementAddresses;
  protected readonly itemPlacementLabelKey = itemPlacementLabelKey;
  protected readonly statusLabelKey = computed(() =>
    itemStatusLabelKey(this.item().status),
  );
  protected readonly statusBadgeClass = computed(() =>
    itemStatusDetailBadgeClass(this.item().status),
  );
  protected readonly dimensionsLabel = computed(() =>
    itemDetailDimensionsLabel(this.item()),
  );
  protected readonly quantityBadge = computed(() => itemQuantityBadge(this.item()));
  protected readonly isInBox = computed(() => Boolean(this.item().box_id));
  protected readonly showDimensionsScopeToggle = computed(
    () => this.canEditAllFields() && !this.isInBox(),
  );
  protected readonly hasPhotos = computed(() => this.photos().length > 0);
  protected readonly hasMultiplePhotos = computed(() => this.photos().length > 1);
  protected readonly activePhotoUrl = computed(
    () => this.photos()[this.activePhotoIndex()] ?? '',
  );
  protected readonly photoCounter = computed(() => {
    const total = this.photos().length;
    if (total <= 1) {
      return '';
    }
    return `${this.activePhotoIndex() + 1} / ${total}`;
  });
  protected readonly editable = computed(
    () => this.data.editable === true && !!this.data.onSaveItem,
  );
  protected readonly deletable = computed(
    () => !!this.data.onDeleteItem && isDeletableCatalogItem(this.item()),
  );
  protected readonly fragileLabelKey = computed(() =>
    this.data.context === 'warehouse_receive'
      ? 'admin.storageRequests.itemPackagingLabel'
      : 'CLIENT.CABINET.ITEM_CATALOG.FIELD_FRAGILE',
  );
  protected readonly canEditAllFields = computed(
    () => this.item().is_standalone,
  );
  protected readonly allowedNextStatuses = computed(
    () => this.item().allowed_next_statuses ?? [],
  );
  protected readonly isWarehouseWorker = computed(() =>
    this.#auth.roles().includes(UserRole.WarehouseWorker),
  );
  protected readonly hasCellPlacement = computed(
    () => (this.item().cells?.length ?? 0) > 0,
  );
  protected readonly resolvedWarehouseId = computed(() => {
    const explicit = this.data.warehouseId?.trim();
    if (explicit) {
      return explicit;
    }
    return this.#profile.userInfo()?.staff?.warehouse_id?.trim() ?? null;
  });
  protected readonly canManageCellPlacement = computed(() => {
    if (!this.isWarehouseWorker() || this.editMode()) {
      return false;
    }
    const current = this.item();
    return (
      current.status === StorageStatus.Stored &&
      current.is_standalone &&
      !current.box_id
    );
  });
  protected readonly showPlacementActions = computed(
    () => this.canManageCellPlacement() && !this.placementPanelOpen(),
  );
  protected readonly showPlaceInCellButton = computed(
    () => this.showPlacementActions() && !this.hasCellPlacement(),
  );
  protected readonly showMoveAndClearButtons = computed(
    () => this.showPlacementActions() && this.hasCellPlacement(),
  );
  protected readonly placementWarehouseActionsDisabled = computed(
    () =>
      !this.resolvedWarehouseId() ||
      this.placementSubmitting() ||
      this.clearingPlacement() ||
      this.updating(),
  );
  protected readonly clearingPlacementDisabled = computed(
    () =>
      this.placementSubmitting() ||
      this.clearingPlacement() ||
      this.updating(),
  );
  protected readonly placementInitialCells = computed(() => {
    if (!this.placementPanelOpen()) {
      return [];
    }
    if (this.placementPanelMode() === 'move') {
      return cellCoordsFromRefs(this.item().cells);
    }
    return [];
  });
  protected readonly placementSubmitLabelKey = computed(() =>
    this.placementPanelMode() === 'move'
      ? 'admin.users.items.moveCell'
      : 'admin.users.items.placeInCell',
  );

  readonly form = this.#fb.nonNullable.group({
    name: [
      '',
      [
        Validators.required,
        Validators.maxLength(CLIENT_ITEM_NAME_MAX_LENGTH),
      ],
    ],
    description: ['', [Validators.maxLength(CLIENT_ITEM_DESCRIPTION_MAX_LENGTH)]],
    quantity: [
      '',
      [Validators.min(ITEM_MIN_QUANTITY), Validators.max(ITEM_MAX_QUANTITY)],
    ],
    dimensions_scope: ['per_unit' as ItemDimensionsScope],
    width_cm: [''],
    height_cm: [''],
    depth_cm: [''],
    weight_kg: [''],
    is_fragile: [false],
    is_sentimental: [false],
    is_disassembled: [false],
    parts_count: [{ value: '', disabled: true }],
  });

  readonly #initialDimensionsScope = signal<ItemDimensionsScope>('per_unit');

  readonly #formValues = toSignal(
    this.form.valueChanges.pipe(startWith(this.form.getRawValue())),
    { initialValue: this.form.getRawValue() },
  );

  protected readonly normalizedQuantity = computed(() =>
    normalizeItemQuantity(this.#formValues().quantity),
  );

  protected readonly scopeChanged = computed(() => {
    if (this.isInBox()) {
      return false;
    }
    return this.#formValues().dimensions_scope !== this.#initialDimensionsScope();
  });

  protected readonly dimensionsHintKey = computed(() => {
    if (this.isInBox()) {
      return 'CLIENT.CABINET.ITEM_CATALOG.DIMENSIONS_IN_BOX_HINT';
    }
    if (this.#formValues().dimensions_scope === 'total') {
      return 'CLIENT.CABINET.ITEM_CATALOG.DIMENSIONS_TOTAL_HINT';
    }
    if (this.normalizedQuantity() > 1) {
      return 'CLIENT.CABINET.ITEM_CATALOG.DIMENSIONS_PER_UNIT_HINT';
    }
    return null;
  });

  protected readonly previewVolumeLabel = computed(() => {
    const volume = previewBillableVolumeM3({
      width_cm: this.#formValues().width_cm,
      height_cm: this.#formValues().height_cm,
      depth_cm: this.#formValues().depth_cm,
      quantity: this.#formValues().quantity,
      dimensions_scope: this.#formValues().dimensions_scope,
      box_id: this.isInBox() ? 'in-box' : null,
    });
    if (volume == null) {
      return null;
    }

    return `${volume.toLocaleString('ru-RU', { maximumFractionDigits: 3 })} м³`;
  });

  constructor() {
    effect(() => {
      const inBox = this.isInBox();
      const scopeControl = this.form.controls.dimensions_scope;
      if (inBox) {
        scopeControl.setValue('per_unit', { emitEvent: false });
        scopeControl.disable({ emitEvent: false });
      } else {
        scopeControl.enable({ emitEvent: false });
      }
    });

    effect(() => {
      const current = this.item();
      const scope = itemEffectiveDimensionsScope(current);
      this.form.patchValue({
        name: current.name,
        description: current.description ?? '',
        quantity:
          (current.quantity ?? ITEM_MIN_QUANTITY) > ITEM_MIN_QUANTITY
            ? String(current.quantity)
            : '',
        dimensions_scope: scope,
        width_cm: current.width_cm?.toString() ?? '',
        height_cm: current.height_cm?.toString() ?? '',
        depth_cm: current.depth_cm?.toString() ?? '',
        weight_kg: current.weight_kg?.toString() ?? '',
        is_fragile: current.is_fragile ?? false,
        is_sentimental: current.is_sentimental ?? false,
        is_disassembled: current.is_disassembled ?? false,
        parts_count: current.parts_count?.toString() ?? '',
      });
      this.#initialDimensionsScope.set(scope);
      this.#syncDisassemblyControls(current.is_disassembled ?? false);
      this.itemTags.set([...(current.tags ?? [])]);
    });
  }

  setDimensionsScope(scope: ItemDimensionsScope): void {
    if (this.isInBox()) {
      return;
    }
    this.form.controls.dimensions_scope.setValue(scope);
  }

  onDisassembledToggle(): void {
    this.#syncDisassemblyControls(this.form.controls.is_disassembled.value ?? false);
  }

  #syncDisassemblyControls(checked: boolean): void {
    const parts = this.form.controls.parts_count;
    if (checked) {
      parts.enable({ emitEvent: false });
      parts.setValidators([
        Validators.required,
        Validators.min(ITEM_MIN_PARTS_COUNT),
        Validators.max(ITEM_MAX_PARTS_COUNT),
      ]);
    } else {
      parts.disable({ emitEvent: false });
      parts.clearValidators();
      parts.setValue('', { emitEvent: false });
    }
    parts.updateValueAndValidity({ emitEvent: false });
  }

  ngOnInit(): void {
    this.#reloadPhotos();
  }

  showPreviousPhoto(): void {
    const total = this.photos().length;
    if (total <= 1) {
      return;
    }
    this.activePhotoIndex.update(
      (index) => (index - 1 + total) % total,
    );
  }

  showNextPhoto(): void {
    const total = this.photos().length;
    if (total <= 1) {
      return;
    }
    this.activePhotoIndex.update((index) => (index + 1) % total);
  }

  goToPhoto(index: number): void {
    if (index >= 0 && index < this.photos().length) {
      this.activePhotoIndex.set(index);
    }
  }

  toggleEditMode(): void {
    const wasEditing = this.editMode();
    this.editMode.update((value) => !value);
    if (wasEditing) {
      this.#reloadPhotos();
    } else {
      this.placementPanelOpen.set(false);
    }
  }

  openPlacementPanel(mode: PlacementPanelMode): void {
    if (this.placementWarehouseActionsDisabled()) {
      return;
    }
    this.placementPanelMode.set(mode);
    this.placementPanelOpen.set(true);
  }

  closePlacementPanel(): void {
    if (this.placementSubmitting()) {
      return;
    }
    this.placementPanelOpen.set(false);
  }

  async onPlacementSubmit(cells: CellCoord[]): Promise<void> {
    const warehouseId = this.resolvedWarehouseId();
    if (!warehouseId || this.placementSubmitting()) {
      return;
    }

    this.placementSubmitting.set(true);
    try {
      const response = await firstValueFrom(
        this.#itemService.placeItemOnCells(this.item().id, {
          warehouse_id: warehouseId,
          cells,
        }),
      );
      const addresses =
        response.cells?.map((cell) => cell.address).join(', ') ||
        response.address ||
        '';
      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'success',
        text: [
          this.#translate.instant('admin.users.items.placementSuccess', {
            address: addresses,
          }),
        ],
      });
      await this.#refreshItem();
      this.placementPanelOpen.set(false);
    } catch (error) {
      this.#notifyPlacementError(error);
    } finally {
      this.placementSubmitting.set(false);
    }
  }

  async onClearPlacement(): Promise<void> {
    if (
      !this.hasCellPlacement() ||
      this.clearingPlacement() ||
      this.clearingPlacementDisabled()
    ) {
      return;
    }

    const confirmed = await this.#confirmDialog.confirm({
      title: this.#translate.instant(
        'admin.users.items.clearFromCellConfirmTitle',
      ),
      message: this.#translate.instant(
        'admin.users.items.clearFromCellConfirmMessage',
      ),
      okLabel: this.#translate.instant('common.yes'),
      cancelLabel: this.#translate.instant('common.actions.cancel'),
    });
    if (!confirmed) {
      return;
    }

    this.clearingPlacement.set(true);
    try {
      await firstValueFrom(
        this.#itemService.clearItemPlacement(this.item().id),
      );
      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'success',
        text: [this.#translate.instant('admin.staffQrScan.clearSuccess')],
      });
      await this.#refreshItem();
    } catch (error) {
      this.#notifyPlacementError(error);
    } finally {
      this.clearingPlacement.set(false);
    }
  }

  #reloadPhotos(): void {
    const fallback = this.data.previewUrl ? [this.data.previewUrl] : [];
    this.photosLoading.set(true);

    this.#photoUpload
      .listItemPhotos(this.data.item.id)
      .pipe(
        catchError(() => of({ data: [], total: 0 })),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe((response) => {
        const urls = (response.data ?? [])
          .slice()
          .sort((left, right) => {
            if (left.is_main !== right.is_main) {
              return Number(right.is_main) - Number(left.is_main);
            }
            return left.sort_order - right.sort_order;
          })
          .map((photo) => photo.url)
          .filter((url): url is string => Boolean(url));

        const resolved = urls.length > 0 ? urls : fallback;
        let startIndex = 0;
        if (this.data.previewUrl && resolved.length > 0) {
          const matchedIndex = resolved.indexOf(this.data.previewUrl);
          if (matchedIndex >= 0) {
            startIndex = matchedIndex;
          }
        }

        this.photos.set(resolved);
        this.activePhotoIndex.set(startIndex);
        this.photosLoading.set(false);
      });
  }

  async #refreshItem(): Promise<void> {
    const current = this.item();
    const refreshed = await firstValueFrom(
      this.#itemService.getAdminItem(current.id),
    );
    const merged: AdminUserItemResponse = {
      ...refreshed,
      in_active_order: refreshed.in_active_order ?? current.in_active_order,
      allowed_next_statuses: current.allowed_next_statuses,
      client_deleted_at: refreshed.client_deleted_at ?? current.client_deleted_at,
    };
    this.item.set(merged);
    this.data.onItemRefreshed?.(merged);
  }

  #notifyPlacementError(error: unknown): void {
    const body =
      error instanceof HttpErrorResponse
        ? (error.error as { error?: string } | null)
        : null;
    this.#notification.addToQueue({
      id: Date.now().toString(),
      type: 'error',
      text: [
        body?.error ??
          this.#translate.instant('admin.warehouses.placement.submitError'),
      ],
    });
  }

  async onSaveItem(): Promise<void> {
    if (!this.data.onSaveItem || this.savingItem()) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.savingItem.set(true);
    try {
      const uploader = this.photoUploader();
      if (uploader) {
        await uploader.uploadAll({ entityId: this.item().id });
      }

      await this.data.onSaveItem(this.#buildSavePayload());
      this.editMode.set(false);
      this.#dialogRef.close(true);
    } finally {
      this.savingItem.set(false);
    }
  }

  async onDeleteItem(): Promise<void> {
    if (
      !this.data.onDeleteItem ||
      !this.deletable() ||
      this.deletingItem() ||
      this.updating() ||
      this.savingItem()
    ) {
      return;
    }

    const confirmed = await this.#confirmDialog.confirm({
      title: this.#translate.instant('admin.users.items.deleteConfirmTitle'),
      message: this.#translate.instant('admin.users.items.deleteConfirmMessage', {
        name: this.item().name,
      }),
      okLabel: this.#translate.instant('admin.users.items.delete'),
      cancelLabel: this.#translate.instant('common.actions.cancel'),
    });
    if (!confirmed) {
      return;
    }

    this.deletingItem.set(true);
    try {
      await this.data.onDeleteItem();
      this.#dialogRef.close(true);
    } catch {
      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'error',
        text: [this.#translate.instant('admin.users.items.deleteError')],
      });
    } finally {
      this.deletingItem.set(false);
    }
  }

  async onChangeStatus(nextStatus: string): Promise<void> {
    if (this.updating()) {
      return;
    }

    this.updating.set(true);
    try {
      await this.data.onChangeStatus(nextStatus);
      this.#dialogRef.close(true);
    } finally {
      this.updating.set(false);
    }
  }

  async onPrintQr(): Promise<void> {
    if (this.printingQr() || this.updating()) {
      return;
    }

    this.printingQr.set(true);
    try {
      const caption = await resolveOwnerLastName(
        this.#userAdmin,
        this.item().user_id,
        this.data.ownerLastName,
      );
      await printStaffEntityQr({
        entityId: this.item().id,
        entityKind: 'item',
        caption,
        printer: this.#printer,
        translate: this.#translate,
        notification: this.#notification,
      });
    } finally {
      this.printingQr.set(false);
    }
  }

  #buildSavePayload(): UpdateStaffItemRequest {
    const raw = this.form.getRawValue();
    const isDisassembled = raw.is_disassembled ?? false;
    const disassemblyFields = {
      is_disassembled: isDisassembled,
      parts_count: isDisassembled
        ? this.#optionalNumber(raw.parts_count)
        : undefined,
    };

    if (!this.canEditAllFields()) {
      return {
        is_fragile: raw.is_fragile,
        is_sentimental: raw.is_sentimental,
        ...disassemblyFields,
      };
    }

    return {
      name: raw.name.trim(),
      description: this.#optionalText(raw.description),
      quantity: this.#buildQuantityPayload(),
      dimensions_scope: this.#buildDimensionsScopePayload(),
      width_cm: this.#optionalNumber(raw.width_cm),
      height_cm: this.#optionalNumber(raw.height_cm),
      depth_cm: this.#optionalNumber(raw.depth_cm),
      weight_kg: this.#optionalNumber(raw.weight_kg),
      is_fragile: raw.is_fragile,
      is_sentimental: raw.is_sentimental,
      tags: this.itemTags(),
      ...disassemblyFields,
    };
  }

  #buildQuantityPayload(): number | undefined {
    const quantity = normalizeItemQuantity(this.form.getRawValue().quantity);
    return quantity > ITEM_MIN_QUANTITY ? quantity : undefined;
  }

  #buildDimensionsScopePayload(): ItemDimensionsScope | undefined {
    if (this.isInBox()) {
      return undefined;
    }
    const scope = this.form.getRawValue().dimensions_scope ?? 'per_unit';
    return scope === 'per_unit' ? undefined : scope;
  }

  #optionalText(value: string): string | undefined {
    const trimmed = value.trim();
    return trimmed ? trimmed : undefined;
  }

  #optionalNumber(value: string): number | undefined {
    const trimmed = value.trim();
    if (!trimmed) {
      return undefined;
    }
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  @HostListener('document:keydown.arrowleft')
  protected onArrowLeft(): void {
    if (!this.editMode() && this.hasMultiplePhotos()) {
      this.showPreviousPhoto();
    }
  }

  @HostListener('document:keydown.arrowright')
  protected onArrowRight(): void {
    if (!this.editMode() && this.hasMultiplePhotos()) {
      this.showNextPhoto();
    }
  }
}
